/* Formulista password page: <fm-password> owns the password window (a modal <dialog> that grows out of the
   password button as a circle and shrinks back into it), the show-password toggle and the photo that follows the
   pointer. The newsletter card inside is a <fm-newsletter> (fm-newsletter.js). Without JavaScript the buttons use
   the native command="show-modal" / command="close" invokers where the browser supports them. */

(() => {
  if (customElements.get('fm-password')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const OPEN_MS = 900;
  const CLOSE_MS = 600;

  class FMPassword extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      /** @type {HTMLDialogElement | null} */
      this.dialog = this.querySelector('[data-fm-pw-dialog]');
      /** @type {HTMLButtonElement | null} */
      this.opener = this.querySelector('[data-fm-pw-open]');
      this.input = this.dialog?.querySelector('input[type="password"]') || null;
      this.peek = this.dialog?.querySelector('[data-fm-pw-peek]') || null;
      this.media = this.querySelector('[data-fm-pw-media]');
      this.trigger = null;

      // The card's seal spins through fm-newsletter.js; without a sign-up form that element stops early.
      const card = this.querySelector('fm-newsletter');
      if (card && !card.querySelector('form') && window.FMListing?.bindSeals) window.FMListing.bindSeals(card, signal);

      if (this.dialog) this.bindDialog(signal);
      if (this.peek && this.input) this.bindPeek(signal);
      if (this.media && this.classList.contains('fm-pw--settle')) this.bindMedia(signal);
    }

    disconnectedCallback() {
      this.controller?.abort();
      window.clearTimeout(this.timer);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    /* ---------- Window ---------- */

    /** @param {AbortSignal} signal */
    bindDialog(signal) {
      const dialog = this.dialog;

      // JavaScript takes over from the native invokers so the window can animate.
      for (const button of this.querySelectorAll('[commandfor]')) {
        button.removeAttribute('commandfor');
        button.removeAttribute('command');
      }

      this.opener?.addEventListener('click', () => this.open(this.opener), { signal });
      dialog.querySelector('[data-fm-pw-close]')?.addEventListener('click', () => this.close(), { signal });

      dialog.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault();
          this.close();
        },
        { signal }
      );

      // A click on the backdrop lands on the <dialog> itself, outside the sheet.
      dialog.addEventListener(
        'click',
        (event) => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          const inside =
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom;
          if (!inside) this.close();
        },
        { signal }
      );

      dialog.querySelector('form')?.addEventListener(
        'submit',
        (event) => {
          if (!this.input || this.input.value.trim()) return;
          event.preventDefault();
          this.shake();
        },
        { signal }
      );

      // After a wrong password Shopify renders the page again with the window open (non-modal without JS).
      // It grows from its own centre then, since the visitor did not just press the button.
      if (dialog.hasAttribute('data-fm-pw-failed')) {
        dialog.close();
        this.open(null, { select: true });
        this.trigger = this.opener;
      }
    }

    /** Places the circle's centre on the trigger, relative to the sheet. */
    setOrigin(trigger) {
      const sheet = this.dialog.querySelector('.fm-pw__sheet');
      if (!sheet) return;
      const box = sheet.getBoundingClientRect();
      let x = box.width / 2;
      let y = box.height / 2;
      if (trigger && trigger.isConnected) {
        const t = trigger.getBoundingClientRect();
        if (t.width || t.height) {
          x = t.left + t.width / 2 - box.left;
          y = t.top + t.height / 2 - box.top;
        }
      }
      const radius = Math.max(
        Math.hypot(x, y),
        Math.hypot(box.width - x, y),
        Math.hypot(x, box.height - y),
        Math.hypot(box.width - x, box.height - y)
      );
      this.dialog.style.setProperty('--fmp-ox', `${x}px`);
      this.dialog.style.setProperty('--fmp-oy', `${y}px`);
      this.dialog.style.setProperty('--fmp-or', `${Math.ceil(radius) + 2}px`);
    }

    /**
     * @param {HTMLElement | null} trigger
     * @param {{ select?: boolean }} [options]
     */
    open(trigger, { select = false } = {}) {
      const dialog = this.dialog;
      if (!dialog) return;
      window.clearTimeout(this.timer);
      this.trigger = trigger || document.activeElement;

      if (!dialog.open) dialog.showModal();
      dialog.removeAttribute('data-closing');
      this.setOrigin(trigger);
      void dialog.offsetWidth;
      dialog.setAttribute('data-open', '');
      this.opener?.setAttribute('aria-expanded', 'true');

      const focus = () => {
        if (!this.input) return;
        this.input.focus({ preventScroll: true });
        if (select) this.input.select();
      };
      if (reducedMotion.matches) focus();
      else this.timer = window.setTimeout(focus, Math.min(OPEN_MS, 420));
    }

    close() {
      const dialog = this.dialog;
      if (!dialog?.open || dialog.hasAttribute('data-closing')) return;
      window.clearTimeout(this.timer);
      this.setOrigin(this.trigger);
      dialog.removeAttribute('data-open');
      dialog.setAttribute('data-closing', '');
      this.opener?.setAttribute('aria-expanded', 'false');

      const finish = () => {
        dialog.removeAttribute('data-closing');
        dialog.close();
        if (this.input && this.peek) this.setPeek(false);
        const back = this.trigger;
        this.trigger = null;
        if (back instanceof HTMLElement && back.isConnected) back.focus({ preventScroll: true });
      };

      if (reducedMotion.matches) finish();
      else this.timer = window.setTimeout(finish, CLOSE_MS);
    }

    shake() {
      const field = this.input?.closest('.fm-pw__pass-field');
      if (!field) return;
      field.classList.remove('is-invalid');
      void field.offsetWidth;
      field.classList.add('is-invalid');
      this.input.focus();
    }

    /* ---------- Show password ---------- */

    /** @param {AbortSignal} signal */
    bindPeek(signal) {
      this.peek.hidden = false;
      this.peek.addEventListener('click', () => this.setPeek(this.input.type === 'password'), { signal });
      this.input.addEventListener(
        'input',
        () => {
          const field = this.input.closest('.fm-pw__pass-field');
          if (!field?.classList.contains('is-invalid')) return;
          field.classList.remove('is-invalid');
          this.input.removeAttribute('aria-invalid');
        },
        { signal }
      );
    }

    /** @param {boolean} visible */
    setPeek(visible) {
      this.input.type = visible ? 'text' : 'password';
      this.peek.setAttribute('aria-pressed', String(visible));
      const label = visible ? this.peek.dataset.hide : this.peek.dataset.show;
      if (label) this.peek.setAttribute('aria-label', label);
    }

    /* ---------- Photo ---------- */

    /** @param {AbortSignal} signal */
    bindMedia(signal) {
      const media = this.media;
      let x = 0;
      let y = 0;

      const paint = () => {
        this.frame = 0;
        media.style.setProperty('--fmp-mx', `${x}px`);
        media.style.setProperty('--fmp-my', `${y}px`);
      };
      const queue = () => {
        if (!this.frame) this.frame = requestAnimationFrame(paint);
      };

      media.addEventListener(
        'pointermove',
        (event) => {
          if (!finePointer.matches || reducedMotion.matches) return;
          const box = media.getBoundingClientRect();
          const range = Math.min(box.width, box.height) * 0.025;
          x = ((event.clientX - box.left) / box.width - 0.5) * -2 * range;
          y = ((event.clientY - box.top) / box.height - 0.5) * -2 * range;
          queue();
        },
        { signal }
      );
      media.addEventListener(
        'pointerleave',
        () => {
          x = 0;
          y = 0;
          queue();
        },
        { signal }
      );
    }
  }

  customElements.define('fm-password', FMPassword);
})();
